import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import PricingTable from "@/components/PricingTable";
import VideoCard from "@/components/VideoCard";
import EligeOpcion from "@/components/EligeOpcion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  ShieldCheck, Phone, MapPin, Star, ArrowRight, Users, Moon, Volume2, Scale, HeartHandshake,
  Award, Truck, BadgeCheck, Sparkles, Clock, ChevronDown, MessageCircle,
  Layers, Wind, ShieldOff, Droplets, Heart, Ruler, ZoomIn, X,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";
const LOGO_WIDE = "https://customer-assets-agu9un31.emergentagent.net/job_media-insights-26/artifacts/dygwa5sx_logocore.png";
const HERO_VIDEO = "https://customer-assets-agu9un31.emergentagent.net/job_media-insights-26/artifacts/dk844324_variasfamilias.mp4";
const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/ywlmq81c_colchon-core-1%20%282%29.jpg";
const ANATOMY_IMG = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jpj77u1y_bg8-2_1.jpg";
const COUPLE_HAPPY = "https://images.unsplash.com/photo-1552346154-21d32810aba3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const COUPLE_TIRED = "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const VIDEO_TECNOLOGIA = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/rn4sm296_descanso-colchon-core.mp4";
const VIDEO_PAREJAS = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/ldvtgdm9_CORE%20COLCHON%20IDEAL%20PARA%20PAREJAS.mp4";
const VIDEO_REFUERZOS = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/7bcww0jc_CAMBIO%20REFUERZOS%20VISCO.mp4";
const VIDEO_COMPOSICION = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/kq7en2yd_composicion-core.mp4";
const WHATSAPP_LINK = "https://api.whatsapp.com/send?phone=34630666734&text=Quiero%20mi%20cup%C3%B3n%20descuento%20de%20100%E2%82%AC%20-%20Colch%C3%B3n%20para%20parejas";
const PAREJAS_TIENDA_TEXT = "Prueba cada firmeza junto a tu pareja en nuestra tienda Muebles Muñoz y elige la combinación ideal.";

export default function ParejasLanding() {
  useEffect(() => {
    document.title = "Colchón para parejas · Firmeza distinta cada lado — Core Colchón";
    // Meta description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "El colchón para parejas con firmeza distinta a cada lado. Tú más blando, tu pareja más firme. Mismo colchón. 100 noches de prueba y cheque de 100€ para canjear en tienda.");
  }, []);

  return (
    <div data-testid="parejas-landing" className="min-h-screen bg-[#F9F8FA]">
      <ParejasHeader />
      <ParejasHero />
      <EligeOpcion id="elige" testid="parejas-elige-opcion" tiendaText={PAREJAS_TIENDA_TEXT} />
      <FormularioSection />
      <ProblemaSection />
      <TecnologiaCoreadapt />
      <VideoSection />
      <AnatomiaDual />
      <AnatomiaPorDentro />
      <VideoGaleriaParejas />
      <EligeOpcion id="elige-mitad" testid="parejas-elige-opcion-mitad" tiendaText={PAREJAS_TIENDA_TEXT} />
      <AntesDespues />
      <MedidasParejas />
      <Garantias />
      <TestimoniosParejas />
      <FAQParejas />
      <EligeOpcion id="elige-final" testid="parejas-elige-opcion-final" tiendaText={PAREJAS_TIENDA_TEXT} />
      <ParejasFooter />
      <StickyMobileCTA />
    </div>
  );
}

// ============ HEADER =============
function ParejasHeader() {
  return (
    <header className="bg-white border-b border-[#E3DDE8] sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="Core Colchón" className="h-10 w-10 object-contain"/>
          <div>
            <div className="font-display font-bold text-[#2E2638] leading-none">CORE COLCHÓN</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Hecho en España</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-[#2E2638] font-semibold">
          <a href="#problema" className="hover:text-[#8B1538]">El problema</a>
          <a href="#anatomia" className="hover:text-[#8B1538]">Cómo funciona</a>
          <a href="#testimonios" className="hover:text-[#8B1538]">Testimonios</a>
          <a href="#elige-final" className="hover:text-[#8B1538]" data-testid="parejas-nav-tienda">Tienda</a>
          <a href="#faq" className="hover:text-[#8B1538]">FAQ</a>
        </nav>
        <a href="#hero-form" className="bg-[#8B1538] hover:bg-[#6A0F2A] text-white text-xs uppercase tracking-wider font-bold px-4 py-2.5 transition-colors">
          Cheque 100€
        </a>
      </div>
    </header>
  );
}

// ============ HERO =============
function ParejasHero() {
  return (
    <section id="hero" className="relative bg-[#2E2638] text-white overflow-hidden">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover opacity-90"
        aria-hidden="true"
        data-testid="parejas-hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#2E2638]/85 via-[#2E2638]/50 to-[#2E2638]/20"/>
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#2E2638]/60 to-transparent"/>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-10 py-14 sm:py-24 lg:py-32">
        <div className="max-w-3xl">
          <img src={LOGO_WIDE} alt="Core Colchón" className="h-14 sm:h-16 object-contain mb-6 brightness-0 invert" data-testid="parejas-hero-logo"/>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight">
            Tú más <span className="text-[#E2B6C5]">blando</span>.<br/>
            Tu pareja más <span className="text-[#E2B6C5]">firme</span>.<br/>
            <span className="text-white/95">Mismo colchón.</span>
          </h1>
          <p className="mt-7 text-xl sm:text-2xl text-white/85 max-w-2xl leading-snug">
            El único colchón que <strong className="text-white">se adapta al cuerpo de cada uno</strong>, sin renunciar a dormir juntos. Cero discusiones. Cero compromisos.
          </p>
          <p className="mt-5 text-lg text-white/70 max-w-xl">
            Pruébalo 100 noches en tu casa. Si no notas la diferencia en tu descanso, te devolvemos el dinero. <strong className="text-white">Sin letra pequeña.</strong>
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#hero-form"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("hero-form");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="parejas-hero-cta"
              className="inline-flex items-center gap-2 bg-[#8B1538] hover:bg-[#6A0F2A] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 shadow-[0_8px_28px_rgba(139,21,56,0.5)] transition-colors"
            >
              Quiero mi cheque de 100 € <ArrowRight size={16}/>
            </a>
            <span className="text-sm text-white/80">Sin compromiso · Canjeable en tienda</span>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-white/85">
            <span className="flex items-center gap-2"><Clock size={16} className="text-[#E2B6C5]"/> 100 noches de prueba</span>
            <span className="flex items-center gap-2"><Award size={16} className="text-[#E2B6C5]"/> 10 años de garantía</span>
            <span className="flex items-center gap-2"><Truck size={16} className="text-[#E2B6C5]"/> Envío y devolución gratis</span>
          </div>

          <div className="mt-6 flex items-center gap-2">
            <div className="flex text-[#FBBC04]">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor"/>)}
            </div>
            <span className="text-sm text-white/85"><strong className="text-white">4,9/5</strong> en Google · Clientes reales de Core Colchón</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ FORMULARIO (sección propia debajo del hero) =============
function FormularioSection() {
  return (
    <section id="hero-form" className="relative py-16 sm:py-20 bg-gradient-to-br from-[#F9F0F3] via-[#F9F8FA] to-[#F9F0F3] border-b border-[#E3DDE8]">
      <div className="max-w-5xl mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Descuento exclusivo · Parejas</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2E2638] leading-tight">
            Consigue <span className="text-[#8B1538]">100 €</span> para vuestro colchón Core.
          </h2>
          <p className="mt-5 text-lg text-[#5C5366] max-w-md">
            Rellena vuestros datos, recibe el cheque en el email y aplicadlo al pagar en nuestra tienda de Navalcarnero.
          </p>

          <ul className="mt-7 space-y-3 text-[#2E2638] text-sm">
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={18}/> <span>Sin compromiso ni pago por adelantado.</span></li>
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={18}/> <span>Válido en todas las medidas y firmezas.</span></li>
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={18}/> <span>Os asesoramos en tienda sin presión de compra.</span></li>
          </ul>
        </div>

        <div className="lg:col-span-6">
          <div className="bg-white text-[#2E2638] p-6 sm:p-8 shadow-[0_20px_60px_rgba(46,38,56,0.15)] border border-[#E3DDE8]" data-testid="parejas-form-card">
            <LeadForm variant="light" idPrefix="parejas-form" source="parejas" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ PROBLEMA + SOLUCIÓN =============
function ProblemaSection() {
  const problemas = [
    { icon: Scale, t: "Tú lo quieres firme, tu pareja lo quiere blando", d: "Vuestros cuerpos son distintos: peso, altura, forma de dormir. Un colchón único no puede darles a los dos lo que necesitan." },
    { icon: Volume2, t: "Los movimientos de tu pareja te despiertan", d: "Cada vez que se gira, notas el vaivén. Y a la mañana siguiente, cansados los dos." },
    { icon: HeartHandshake, t: "No encontráis un colchón que os guste a los dos", d: "Uno lo quiere duro, el otro blando. Al final compráis el que \"menos mal\" os viene a los dos." },
  ];
  return (
    <section id="problema" className="py-20 sm:py-28 bg-[#F9F8FA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">El problema de las parejas</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            El colchón perfecto para uno es <span className="text-[#8B1538]">el problema del otro</span>.
          </h2>
          <p className="mt-5 text-lg text-[#5C5366]">
            Todas las parejas se han encontrado en esta situación. Y la mayoría acaba conformándose. Nosotros no lo aceptamos.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {problemas.map((p, i) => (
            <article key={p.t} className="bg-white border border-[#E3DDE8] p-7 hover:border-[#8B1538] hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-[#F9F0F3] flex items-center justify-center text-[#8B1538] mb-4">
                <p.icon size={22}/>
              </div>
              <h3 className="font-display text-xl font-bold text-[#2E2638] leading-tight">{p.t}</h3>
              <p className="text-sm text-[#5C5366] mt-2 leading-relaxed">{p.d}</p>
            </article>
          ))}
        </div>

        <div className="mt-14 bg-[#2E2638] text-white p-8 sm:p-12 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E2B6C5] mb-2">La solución Core</div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
              Un colchón. Dos firmezas. <span className="text-[#E2B6C5]">Cero discusiones.</span>
            </h3>
            <p className="mt-3 text-white/80 text-base sm:text-lg">
              Core lleva refuerzos intercambiables en la zona lumbar de cada lado. Tú eliges tu firmeza, tu pareja la suya. En menos de dos minutos, sin herramientas.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a href="#hero-form" className="w-full md:w-auto px-6 py-4 bg-[#8B1538] hover:bg-[#6A0F2A] text-white font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 justify-center transition-colors">
              Quiero mi cheque de 100€ <ArrowRight size={16}/>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ TECNOLOGÍA COREADAPT (con vídeo del responsable) =============
function TecnologiaCoreadapt() {
  return (
    <section id="tecnologia" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B1538] mb-3">La tecnología COREADAPT®</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            El único colchón con <span className="text-[#8B1538]">firmeza regulable</span> en casa. Para los dos.
          </h2>
          <p className="mt-6 text-lg text-[#5C5366] leading-relaxed">
            Cada cuerpo pide una cosa: si tú pesas menos y tu pareja más (o al revés), un colchón único os obliga a un compromiso. Con Core, <strong className="text-[#2E2638]">cada uno tiene su firmeza</strong> en su lado, gracias a los refuerzos intercambiables de la zona lumbar. Sin herramientas, en menos de dos minutos.
          </p>
          <ul className="mt-7 space-y-3 text-[#2E2638]">
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={20}/> <span><strong>Tres firmezas</strong> en un solo colchón: blanda, intermedia y firme.</span></li>
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={20}/> <span><strong>Cada lado de la cama</strong>, a tu gusto y al de tu pareja.</span></li>
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={20}/> <span>Recibes los <strong>tres refuerzos</strong> para probar hasta encontrar los vuestros.</span></li>
            <li className="flex items-start gap-3"><BadgeCheck className="text-[#8B1538] mt-0.5 shrink-0" size={20}/> <span>25 cm de altura, viscoelástica <strong>AIRGEL-75 transpirable</strong>, núcleo Oxigem® doble.</span></li>
          </ul>
          <div className="mt-9">
            <a href="#hero-form" className="inline-flex items-center gap-2 px-7 py-4 bg-[#2E2638] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#1f1827] transition-all">
              Pídelo con 100€ de descuento <ArrowRight size={16}/>
            </a>
          </div>
        </div>
        <div className="relative">
          <VideoCard
            src={VIDEO_TECNOLOGIA}
            title="Cómo funciona COREADAPT®"
            subtitle="Jesús Sánchez, responsable de Core, te lo explica"
            aspect="aspect-[4/5]"
            testid="parejas-video-tecnologia"
          />
          <div className="absolute top-6 right-6 bg-white/95 px-5 py-3 border border-[#E3DDE8] shadow-sm pointer-events-none">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Patentado</div>
            <div className="text-sm font-semibold text-[#2E2638]">U2021-30321 · U2021-31816</div>
          </div>
          <div className="absolute -bottom-6 -left-6 bg-[#8B1538] text-white px-6 py-5 max-w-[260px] pointer-events-none">
            <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">Para vosotros</div>
            <div className="font-display text-xl font-bold mt-1">Firmeza independiente lado a lado</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ VIDEO =============
function VideoSection() {
  return (
    <section className="py-20 sm:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Cómo funciona en 90 segundos</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Por qué Core es <span className="text-[#8B1538]">el colchón de las parejas</span>.
          </h2>
        </div>
        <VideoCard
          src={VIDEO_PAREJAS}
          title="Core Colchón · Ideal para parejas"
          subtitle="Firmeza distinta en cada lado, misma base"
          aspect="aspect-video"
          accent="dark"
          testid="parejas-video-main"
        />
      </div>
    </section>
  );
}

// ============ ANATOMÍA DUAL =============
function AnatomiaDual() {
  return (
    <section id="anatomia" className="py-20 sm:py-28 bg-[#F9F8FA]">
      <div className="max-w-6xl mx-auto px-5 sm:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">La zona Core</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Firmeza <span className="text-[#8B1538]">independiente</span><br/>por lado.
          </h2>
          <p className="mt-6 text-lg text-[#5C5366] leading-relaxed">
            En la zona lumbar de cada lado del colchón, Core lleva un refuerzo intercambiable. Puedes elegir cualquier combinación:
          </p>

          <ul className="mt-6 space-y-4">
            {[
              { l: "Blando", d: "Perfecto si eres de complexión ligera o duermes de lado." },
              { l: "Medio", d: "El equilibrio para complexión estándar y postura mixta." },
              { l: "Firme", d: "Ideal para complexión más alta/pesada o si prefieres dureza." },
            ].map((f) => (
              <li key={f.l} className="flex items-start gap-4">
                <span className="shrink-0 w-2 h-2 bg-[#8B1538] mt-2"/>
                <div>
                  <div className="font-bold text-[#2E2638]">{f.l}</div>
                  <div className="text-sm text-[#5C5366]">{f.d}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 p-5 bg-white border border-[#E3DDE8]">
            <p className="text-sm text-[#5C5366]">
              💡 <strong className="text-[#2E2638]">Cambias tu refuerzo en casa</strong>, con facilidad, en menos de dos minutos. Si tu forma de dormir cambia, tu colchón se adapta contigo.
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="aspect-square bg-cover bg-center relative overflow-hidden" style={{ backgroundImage: `url(${PRODUCT_IMG})` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E2638]/70 via-transparent to-transparent"/>
            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-2 gap-3">
              <div className="bg-white/95 p-4 text-center backdrop-blur">
                <div className="text-[9px] uppercase tracking-wider text-[#5C5366] font-bold">Su lado</div>
                <div className="font-display text-2xl font-bold text-[#8B1538] mt-1">BLANDO</div>
              </div>
              <div className="bg-[#2E2638] text-white p-4 text-center">
                <div className="text-[9px] uppercase tracking-wider text-[#E2B6C5] font-bold">Tu lado</div>
                <div className="font-display text-2xl font-bold mt-1">FIRME</div>
              </div>
            </div>
            <div className="absolute top-6 left-6 bg-white/95 px-3 py-1.5 text-[9px] uppercase tracking-wider font-bold text-[#8B1538]">Colchón Core · Vista lateral</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ ANATOMÍA POR DENTRO (igual estructura que la home) =============
function AnatomiaPorDentro() {
  const layers = [
    { icon: Wind,       title: "Viscoelástica AIRGEL",         desc: "Capa superior con micropartículas de gel. Adaptable y fresca al contacto. Ideal si uno de vosotros es caluroso al dormir." },
    { icon: Layers,     title: "OXIGEM® High",                  desc: "Núcleo intermedio con adaptación progresiva, sin atrapamiento. Absorbe los movimientos del otro para que no os despertéis." },
    { icon: Ruler,      title: "Anatómico zonal",               desc: "Diseño por zonas que respeta la alineación de la columna, sea cual sea vuestra postura al dormir." },
    { icon: Heart,      title: "Refuerzo lumbar (zona Core)",   desc: "Sistema patentado. Aquí es donde tú y tu pareja elegís cada uno vuestra firmeza. Intercambiable en 2 minutos." },
    { icon: BadgeCheck, title: "OXIGEM® 32",                    desc: "Cimiento del colchón. Estructura compacta que aporta durabilidad. 10 años de garantía respaldan la fabricación española." },
    { icon: Droplets,   title: "Canalizaciones transversales",  desc: "Evitan la condensación y mantienen el colchón fresco noche tras noche. Circulación de aire entre las capas." },
    { icon: ShieldOff,  title: "Funda desmontable y lavable",   desc: "Con tratamientos Coolfresh, antiácaros, antibacteriano y antimicrobiano. Inferior 3D antideslizante. Lavable en lavadora." },
  ];

  const badges = [
    { label: "Regulable",              desc: "Tres firmezas en un mismo colchón." },
    { label: "Firmeza a cada lado",    desc: "Independencia entre tu lado y el suyo." },
    { label: "Progresivo",             desc: "Triple núcleo con adaptación gradual." },
    { label: "Anatómico",              desc: "Núcleo zonal que respeta las formas del cuerpo." },
    { label: "Fresco y transpirable",  desc: "Núcleos de poro abierto + VISCO AIRGEL-75." },
  ];

  return (
    <section id="por-dentro" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B1538] mb-3">Core por dentro</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Capas que <span className="text-[#8B1538]">cuidan el descanso de los dos</span>.
          </h2>
          <p className="mt-5 text-lg text-[#5C5366]">
            Cada capa de Core tiene una misión. Juntas, hacen un colchón <strong>regulable, anatómico, fresco y duradero</strong> — pensado para que dos personas duerman como una, aunque sean muy distintas. Esto es lo que ves cuando lo abres por dentro.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          {/* Imagen GRANDE con las etiquetas */}
          <div className="lg:col-span-12">
            <div className="relative bg-gradient-to-br from-[#F9F8FA] to-[#F0EDF4] border border-[#E3DDE8] p-4 sm:p-12">
              <AnatomyImageZoom src={ANATOMY_IMG}/>
              <div className="absolute top-4 left-4 sm:top-8 sm:left-8 bg-white/95 backdrop-blur px-4 py-2 border border-[#E3DDE8] text-[10px] uppercase tracking-[0.2em] font-bold text-[#8B1538] hidden sm:block">
                Patentes U2021-30321 · U2021-31816
              </div>
              <div className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 bg-[#8B1538] text-white px-4 py-3 hidden sm:block pointer-events-none">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Altura</div>
                <div className="font-display text-lg font-bold">25 cm</div>
              </div>
            </div>
          </div>

          {/* Capas debajo en 3 columnas */}
          <div className="lg:col-span-12">
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7 mt-4">
              {layers.map(({ icon: Icon, title, desc }, i) => (
                <li key={title} className="flex gap-4 group" data-testid={`parejas-anatomy-layer-${i}`}>
                  <div className="shrink-0 w-11 h-11 flex items-center justify-center bg-[#8B1538]/10 text-[#8B1538] group-hover:bg-[#8B1538] group-hover:text-white transition-colors">
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#2E2638] text-base">{title}</div>
                    <div className="text-[#5C5366] text-sm mt-1">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {badges.map((b, i) => (
            <div key={b.label} data-testid={`parejas-anatomy-badge-${i}`} className="bg-[#F9F8FA] border border-[#E3DDE8] p-5 text-center hover:border-[#8B1538] transition-colors">
              <div className="font-display font-bold text-[#8B1538] uppercase tracking-wider text-xs mb-2">{b.label}</div>
              <div className="text-[#5C5366] text-sm leading-snug">{b.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ GALERÍA DE VÍDEOS =============
function VideoGaleriaParejas() {
  return (
    <section className="py-20 sm:py-24 bg-[#F9F8FA]">
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Vídeos de producto</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Descubre cómo se cambia y qué hay dentro de Core.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <VideoCard src={VIDEO_REFUERZOS} title="Cómo se cambian los refuerzos" subtitle="Menos de dos minutos, en casa" aspect="aspect-video" accent="dark" testid="parejas-video-refuerzos"/>
          <VideoCard src={VIDEO_COMPOSICION} title="La composición de Core" subtitle="Capa a capa, sin secretos"     aspect="aspect-video" accent="dark" testid="parejas-video-composicion"/>
        </div>
      </div>
    </section>
  );
}

// ============ COMPARATIVA COLCHÓN NORMAL vs CORE =============
function AntesDespues() {
  const filas = [
    { f: "Firmeza distinta para cada persona en la cama", n: false, c: true },
    { f: "Sistema de refuerzos intercambiables en casa",  n: false, c: true },
    { f: "Absorción real de movimientos de la pareja",    n: false, c: true },
    { f: "Núcleo transpirable con canalizaciones",        n: false, c: true },
    { f: "Viscoelástica con gel refrigerante (AIRGEL)",   n: false, c: true },
    { f: "Funda desmontable, lavable y con tratamientos", n: false, c: true },
    { f: "10 años de garantía y fabricación en España",   n: false, c: true },
    { f: "100 noches de prueba en casa con devolución gratuita", n: false, c: true },
  ];
  return (
    <section id="comparativa" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Comparativa</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Un colchón normal <span className="text-[#8B1538]">frente a Core</span>.
          </h2>
          <p className="mt-4 text-lg text-[#5C5366]">
            La diferencia es visible antes de tumbarse. Y se nota desde la primera noche.
          </p>
        </div>

        <div className="border border-[#E3DDE8] overflow-hidden">
          <div className="grid grid-cols-12 bg-[#F9F8FA] border-b border-[#E3DDE8]">
            <div className="col-span-6 sm:col-span-8 px-5 py-4 text-[10px] uppercase tracking-[0.2em] font-bold text-[#5C5366]">Característica</div>
            <div className="col-span-3 sm:col-span-2 px-3 py-4 text-center text-[10px] uppercase tracking-[0.2em] font-bold text-[#5C5366]">Colchón normal</div>
            <div className="col-span-3 sm:col-span-2 px-3 py-4 text-center text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-[#8B1538]">Core Colchón</div>
          </div>
          {filas.map((row, i) => (
            <div key={i} className={`grid grid-cols-12 items-center ${i % 2 === 0 ? "bg-white" : "bg-[#F9F8FA]"}`}>
              <div className="col-span-6 sm:col-span-8 px-5 py-4 text-sm sm:text-base text-[#2E2638]">{row.f}</div>
              <div className="col-span-3 sm:col-span-2 px-3 py-4 text-center">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F9F0F3] text-[#B5AFBE] font-bold">✕</span>
              </div>
              <div className="col-span-3 sm:col-span-2 px-3 py-4 text-center bg-[#8B1538]/5">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#8B1538] text-white">
                  <BadgeCheck size={16}/>
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a href="#hero-form" className="inline-flex items-center gap-2 px-7 py-4 bg-[#8B1538] hover:bg-[#6A0F2A] text-white font-bold uppercase tracking-wider text-sm transition-colors">
            Quiero mi cheque de 100 € <ArrowRight size={16}/>
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ TESTIMONIOS GOOGLE BUSINESS =============
function TestimoniosParejas() {
  // Opiniones reales de clientes recopiladas de la ficha de Google Business de Core Colchón
  const reviews = [
    {
      name: "Charo Prada", avatar: "C", color: "bg-emerald-600", when: "hace 2 semanas",
      text: "En Core Colchón me atendieron con una amabilidad excelente y me ayudaron a elegir el colchón perfecto para mí. La calidad del producto es impresionante: firme, cómodo y con una tecnología innovadora que se adapta perfectamente al cuerpo. Se nota la atención al detalle y el compromiso con la salud del sueño. Definitivamente, una experiencia de compra excelente. ¡Lo recomiendo sin duda!",
    },
    {
      name: "María fe Ligero Luis", avatar: "M", color: "bg-[#5C5366]", when: "Hace un mes", tag: "9 opiniones · 6 fotos",
      text: "5 estrellas son pocas para valorar Core Colchón. Compramos un canapé articulado con colchón core. Desde el minuto 1 la atención fue de 10 con Alejandro. En todo momento las explicaciones superaron nuestras expectativas. Y la entrega fue más rápida de lo que proyectaron en un principio. Los montadores, Ariel y Luis, fueron en todo momento unos grandes profesionales. Estamos encantados con el colchón y con el canapé. ¡Os lo recomendamos! No dudéis en ir a esta tienda: calidad/precio no tiene parangón. Y ya la atención… no hay palabras para describirla. Mil gracias por todo.",
    },
    {
      name: "Alberto Panadero Tapia", avatar: "A", color: "bg-[#8B1538]", when: "hace 1 mes",
      text: "Muy contento con la compra del colchón y del cabecero, se nota la calidad y en la tienda te explican de manera muy detallada todos los procesos del colchón. Cristian nos ayudó de manera exquisita. Muy agradecido y muy contentos con la compra.",
    },
    {
      name: "yolanda arce", avatar: "Y", color: "bg-[#E0A500]", when: "Hace un año", tag: "Local Guide · 56 opiniones · 22 fotos",
      text: "Fuimos a informarnos sobre una cama articulada, entonces nos atendió Alejandro, que nos explicó muy bien todos los tipos de colchones que existían y cuál podría venir mejor según nuestra circunstancia. Los tipos de canapés articulados que había. Nos encantó y pudimos elegir el que mejor nos convenía. Cuando nos llegó, y lo montaron quedó genial, y después de probarlo estamos más encantados aún. Volveremos a comprar allí, son profesionales y saben aconsejarte sobre lo que necesitas. Y sobre todo, siempre con una sonrisa.",
    },
    {
      name: "Miguel Alcaraz Martin", avatar: "M", color: "bg-[#2E2638]", when: "hace 1 mes",
      text: "Recomiendo mucho este colchón. Es de las mejores compras que he hecho. Después de mucho tiempo durmiendo mal, me despierto descansado. Un plus también es que no hace ruido ninguno con los movimientos de mi pareja.",
    },
    {
      name: "A J", avatar: "A", color: "bg-teal-600", when: "Hace 6 meses", tag: "Local Guide · 47 opiniones",
      text: "Adquirimos un Corecolchon y estamos encantados con él. Recibimos una información super completa del producto antes de adquirirlo, pudimos probarlo en la propia tienda, y un servicio estupendo, tanto en la venta como en la entrega del mismo. Recomendados totalmente acudir a LTDC si se está interesado en la compra de un colchón.",
    },
    {
      name: "David de la Fuente García", avatar: "D", color: "bg-pink-600", when: "Hace 3 meses", tag: "6 opiniones",
      text: "Atención de 10 por parte de Alejandro a la hora de comprar un sommier y un colchón. Salí de allí sabiendo perfectamente que había comprado. Los plazos de entrega fueron tal cuál me dijeron. El montaje perfecto.",
    },
    {
      name: "Lika Curelea", avatar: "L", color: "bg-orange-600", when: "hace 4 meses",
      text: "Muy contenta con el colchón y almohada. Ya llevo un tiempo utilizándolos y noto la diferencia. Duermo estupendo. Y el trato de los vendedores es fantástico. Muy amables, atentos y educados. Los productos que ofrecen son de una calidad excepcional. Repetiré. Recomiendo 100%.",
    },
    {
      name: "Sara", avatar: "S", color: "bg-purple-700", when: "hace 2 meses",
      text: "Un colchón excelente. Han estado atentos a lo que necesitábamos, nos han explicado todo con detalle y hemos podido probarlo. La verdad, no hemos tenido ningún problema y estamos disfrutando del descanso que necesitábamos.",
    },
    {
      name: "Alberto Panadero Tapia", avatar: "A", color: "bg-[#8B1538]", when: "hace 2 semanas", tag: "Nueva",
      text: "Fuimos mi novia y yo a preguntar sobre el colchón CORE, porque vimos un anuncio. Desde que entramos en la tienda, el personal es muy atento; nos atendió un señor dedicado exclusivamente a colchones. Nos explicó todo al detalle, pudimos probar el colchón unos minutos, los tipos de firmeza… fue tan buena experiencia que en ese mismo instante lo compramos, y nos llegó tres días más tarde a casa. Súper contentos con la compra y con la atención. Además nos aplicaron una oferta y salió muy bien de precio. Tenemos 3 meses de prueba: si no nos encaja, nos devolverían el dinero íntegro. Eso te dice que es un buen colchón. Nuestro descanso ha mejorado infinitamente.",
    },
    {
      name: "Miguel Alcaraz Martin", avatar: "M", color: "bg-teal-700", when: "hace 4 meses", tag: "Local Guide · 12 opiniones · 8 fotos",
      text: "He comprado 3 camas en 2 meses y el producto ha sido tal y como se ve en la exposición, sin sorpresas negativas. Las camas tienen un acabado impecable y el espacio que ofrecen está muy optimizado. Al hacer la altura de las patas y del canapé a medida, el resultado es excelente para poder limpiar debajo. Los montadores (Ariel) han sido muy cuidadosos, y a pesar de que en la segunda instalación surgieron adversidades, se portaron muy profesionalmente y en 3 días solucionaron los problemas. Estoy muy satisfecho.",
    },
    {
      name: "Lika Curelea", avatar: "L", color: "bg-rose-600", when: "hace 6 meses", tag: "2 opiniones · 1 foto",
      text: "Hemos comprado dos camas articuladas y dos colchones. Todo muy buena calidad. Y la atención de Alejandro de 10. Entrega y montaje muy bien. Muy buena tienda con muy buena atención, de las que quedan pocas. Gracias por todo, un saludo.",
    },
    {
      name: "Sara", avatar: "S", color: "bg-indigo-600", when: "hace 5 meses", tag: "Local Guide · 35 opiniones · 5 fotos",
      text: "He ido a la tienda esta mañana con mi madre para comprar un somier articulado con colchón y barandilla, y hemos salido muy contentas. Jesús nos ha atendido fenomenal, explicándonos todo perfectamente y respondiendo genial a nuestras dudas. Me he ido con la sensación de que es un profesional con muchos años de experiencia en quien se puede confiar. Además, los productos que hemos adquirido nos han parecido de gran calidad y a mejor precio que en otras tiendas consultadas. Sin duda lo recomiendo.",
    },
    {
      name: "Josemi Leon Alia", avatar: "J", color: "bg-blue-700", when: "hace 2 meses (editado)", tag: "9 opiniones",
      text: "Trato excelente. No me sentí del todo cómodo con el colchón que les compré y me ofrecieron devolverme el dinero o coger otro distinto. Ayer me llegó el nuevo, muy contento. El colchón nuevo genial, muchas gracias por todo.",
    },
    {
      name: "kartmant 197", avatar: "K", color: "bg-amber-700", when: "hace 5 meses", tag: "2 opiniones · 2 fotos",
      text: "Pues lo que cualquier cliente pide cuando va a efectuar una compra: profesionalidad en todos los sentidos… honestidad, buen asesoramiento ante lo que uno desconoce, atención, rapidez, eficacia… en fin, muy contento con la compra y el trato recibido.",
    },
    {
      name: "Paco M", avatar: "P", color: "bg-slate-700", when: "hace 7 meses", tag: "Local Guide · 45 opiniones · 2 fotos",
      text: "En la tienda, el señor Alberto nos atendió y explicó todo referente a lo que compramos, sabiendo perfectamente lo que era cada artículo. Los montadores, unos profesionales, montando el material que nos sirvieron y limpiando lo poco que mancharon. Gracias, volveremos y recomendaremos.",
    },
    {
      name: "Juan Carlos Lázaro", avatar: "J", color: "bg-cyan-700", when: "hace 11 meses", tag: "Local Guide · 133 opiniones · 815 fotos",
      text: "Atención inmejorable, tanto en la tienda por parte de Alejandro, que aclaró todas nuestras dudas y buscó soluciones para nuestros inconvenientes, como en la instalación en nuestro domicilio, ya que se adelantaron sobre la hora prevista para el montaje y fueron muy rápidos y eficaces. Incluso tuvieron que subir parte del canapé por la escalera hasta el piso 16 porque no cabía en el ascensor. Dejaron el canapé perfectamente montado y todo limpio y recogido. Unos verdaderos profesionales. El canapé ha quedado estupendamente en el dormitorio, muchísimas gracias.",
    },
    {
      name: "Charo Prada", avatar: "C", color: "bg-fuchsia-700", when: "hace 10 meses", tag: "3 opiniones · 2 fotos",
      text: "Ha sido un placer tratar con esta empresa, tanto en la venta como cuando hicieron el montaje. Personas súper amables que además conocían perfectamente lo que vendían. Los instaladores en mi casa también fenomenal. Para mí la experiencia ha sido de 10 sobre 10. Les doy las gracias.",
    },
  ];

  return (
    <section id="testimonios" className="py-24 sm:py-32 bg-[#F9F8FA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Opiniones reales · Google</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Lo que dicen quienes ya <span className="text-[#8B1538]">duermen en Core</span>.
          </h2>
          <div className="mt-5 inline-flex items-center gap-3 bg-white border border-[#E3DDE8] px-4 py-2.5">
            <span className="font-display font-bold text-[#2E2638] text-2xl">4,9</span>
            <div className="flex text-[#FBBC04]">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor"/>)}
            </div>
            <span className="text-xs text-[#5C5366]">Valoraciones verificadas en Google</span>
          </div>
        </div>

        {/* Carrusel horizontal con scroll táctil */}
        <div className="relative -mx-5 sm:-mx-10">
          <div className="overflow-x-auto snap-x snap-mandatory scroll-smooth px-5 sm:px-10 pb-6 flex gap-5" style={{ scrollbarWidth: "thin" }}>
            {reviews.map((r, i) => (
              <article key={`${r.name}-${i}`} className="snap-start shrink-0 w-[92%] sm:w-[420px] bg-white border border-[#E3DDE8] p-6 flex flex-col shadow-[0_4px_20px_rgba(46,38,56,0.05)]" data-testid={`review-card-${i}`}>
                <header className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 ${r.color} text-white font-display font-bold text-lg flex items-center justify-center rounded-full`}>
                    {r.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#2E2638] text-sm truncate">{r.name}</div>
                    <div className="text-xs text-[#5C5366]">{r.tag ? `${r.tag} · ` : ""}{r.when}</div>
                  </div>
                  {/* Logo Google */}
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </header>
                <div className="flex text-[#FBBC04] mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor"/>)}
                </div>
                <p className="text-sm text-[#2E2638] leading-relaxed flex-1">{r.text}</p>
              </article>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-[#5C5366] mt-4">
          Opiniones recopiladas de la ficha de Google Business Profile de Core Colchón · Clientes reales verificados por Google.
        </p>
      </div>
    </section>
  );
}

// ============ MEDIDAS PARA PAREJAS =============
function MedidasParejas() {
  const medidas = [
    { nombre: "135 x 190", desc: "Matrimonio compacto", pvp: 649, final: 549 },
    { nombre: "150 x 190", desc: "Matrimonio estándar",  pvp: 687, final: 587 },
    { nombre: "180 x 200", desc: "King size",             pvp: 914, final: 814 },
  ];
  return (
    <section id="medidas" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Medidas para vosotros</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            ¿Qué medida necesitas?<br/>
            <span className="text-[#8B1538]">Los 100 € ya están dentro.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {medidas.map((m, i) => (
            <article key={m.nombre} className={`p-6 border ${i === 1 ? "bg-[#2E2638] text-white border-[#2E2638]" : "bg-white border-[#E3DDE8]"}`}>
              {i === 1 && <div className="text-[10px] uppercase tracking-wider font-bold text-[#E2B6C5] mb-2">El más vendido</div>}
              <div className={`font-display text-2xl font-bold ${i === 1 ? "text-white" : "text-[#2E2638]"}`}>{m.nombre}</div>
              <div className={`text-sm ${i === 1 ? "text-white/70" : "text-[#5C5366]"}`}>{m.desc}</div>

              <div className="mt-6 flex items-baseline gap-3">
                <div className={`font-display text-4xl font-bold ${i === 1 ? "text-white" : "text-[#8B1538]"}`}>{m.final} €</div>
                <div className={`text-sm line-through ${i === 1 ? "text-white/50" : "text-[#B5AFBE]"}`}>{m.pvp} €</div>
              </div>
              <div className={`text-xs mt-1 uppercase tracking-wider font-bold ${i === 1 ? "text-[#E2B6C5]" : "text-emerald-700"}`}>Ahorro directo de 100 €</div>

              <a href="#hero-form" className={`mt-6 block text-center px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors ${i === 1 ? "bg-white text-[#2E2638] hover:bg-[#E2B6C5]" : "bg-[#8B1538] text-white hover:bg-[#6A0F2A]"}`}>
                Quiero esta medida →
              </a>
            </article>
          ))}
        </div>

        <PricingTable testidPrefix="parejas-pricing" />

        <div data-testid="parejas-mas-medidas" className="mt-6 text-center text-sm text-[#5C5366]">
          ¿Necesitáis una medida especial? Indícalo en el <a href="#hero-form" className="text-[#8B1538] font-bold underline underline-offset-2 hover:text-[#6A0F2A]">formulario</a> y os asesoramos sin compromiso.
        </div>

        <p className="text-center text-xs text-[#5C5366] mt-6">Precio final tras aplicar el cheque de 100€ en tienda. Cheque canjeable únicamente en la tienda Core Colchón de Navalcarnero.</p>
      </div>
    </section>
  );
}

// ============ GARANTÍAS =============
function Garantias() {
  const items = [
    { icon: Clock, t: "100 noches de prueba", d: "En vuestra cama, no en tienda." },
    { icon: Award, t: "10 años de garantía", d: "Estamos detrás de cada colchón." },
    { icon: BadgeCheck, t: "Hecho en España", d: "Fabricación propia en Madrid." },
    { icon: Truck, t: "Envío y devolución gratis", d: "Sin coste añadido, sin letra pequeña." },
  ];
  return (
    <section className="py-16 bg-[#F9F8FA] border-y border-[#E3DDE8]">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <div key={it.t} className="flex items-start gap-3">
            <div className="shrink-0 w-11 h-11 bg-[#8B1538] text-white flex items-center justify-center">
              <it.icon size={18}/>
            </div>
            <div>
              <div className="font-bold text-[#2E2638] text-sm">{it.t}</div>
              <div className="text-xs text-[#5C5366] mt-0.5">{it.d}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============ FAQ =============
function FAQParejas() {
  const preguntas = [
    { q: "¿Y si uno pesa 60 kg y el otro 100 kg?", a: "Perfecto para Core. Cada uno elige su refuerzo (blando, medio, firme) para su lado. Si vuestros pesos son muy distintos, es cuando más se nota la diferencia frente a un colchón normal — porque cada cuerpo recibe exactamente la firmeza que necesita." },
    { q: "¿Se nota la línea entre los dos lados?", a: "No. La transición es imperceptible: no hay hendidura ni bulto central. Sobre los refuerzos va la misma capa continua de viscoelástica AIRGEL y acolchado. Notaréis diferencia de firmeza al presionar cada lado, pero al tumbaros es totalmente continuo." },
    { q: "¿Podemos cambiar la firmeza después?", a: "Sí, cuando queráis. Los refuerzos se sacan y se cambian en menos de dos minutos, con facilidad. Si tu forma de dormir cambia (embarazo, cambio de peso, temporada de más calor…), tu lado del colchón se adapta contigo." },
    { q: "¿Notaré cuando mi pareja se mueva por la noche?", a: "Casi nada. Las capas de OXIGEM® y visco AIRGEL están diseñadas para absorber los movimientos: la energía se disipa en el núcleo en lugar de propagarse. Es la queja número uno de las parejas con colchones normales, y con Core desaparece." },
    { q: "¿Vale para camas articuladas o con canapé?", a: "Sí, Core es compatible con canapé abatible, somier de láminas y bases de cama articuladas (dentro de las medidas estándar). Se adapta sin problema y mantiene la firmeza regulable." },
    { q: "¿Cómo funcionan las 100 noches?", a: "Os lleváis el colchón a casa y lo probáis 100 noches en vuestra rutina real. Si al final de ese periodo no os convence, lo recogemos y os devolvemos el 100% del dinero. Sin coste de envío, sin letra pequeña." },
    { q: "¿Está fabricado en España?", a: "Sí. Core Colchón se fabrica íntegramente en España, con materiales de primera calidad y patentes propias (U2021-30321 y U2021-31816). Cada colchón lleva 10 años de garantía respaldados por Muebles de Siempre S.L." },
  ];
  return (
    <section id="faq" className="py-20 sm:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-5 sm:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">Preguntas frecuentes</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2E2638] leading-tight">
            Lo que suelen preguntarnos <span className="text-[#8B1538]">las parejas</span>.
          </h2>
        </div>

        <div className="space-y-3">
          {preguntas.map((p, i) => (
            <details key={p.q} className="group border border-[#E3DDE8] bg-white p-5 open:border-[#8B1538] transition-colors">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-bold text-[#2E2638] pr-4">{p.q}</span>
                <ChevronDown size={18} className="text-[#8B1538] transition-transform group-open:rotate-180 shrink-0"/>
              </summary>
              <p className="mt-3 text-[#5C5366] leading-relaxed">{p.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ ANATOMY IMAGE ZOOM (Dialog / lightbox) =============
function AnatomyImageZoom({ src }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="parejas-anatomy-zoom-btn"
        className="group relative w-full block cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#8B1538] focus:ring-offset-2"
        aria-label="Ampliar detalle del colchón Core por dentro"
      >
        <img
          src={src}
          alt="Sección del colchón Core con todas sus capas: viscoelástica AIRGEL, OXIGEM High, anatómico zonal, OXIGEM 32, refuerzo lumbar y funda con tratamientos Coolfresh, antiácaros, antibacteriano y antimicrobiano"
          className="w-full h-auto max-w-5xl mx-auto block"
          loading="lazy"
          data-testid="parejas-anatomy-image"
        />
        <span className="pointer-events-none absolute bottom-3 right-3 sm:hidden inline-flex items-center gap-1.5 bg-[#2E2638]/90 text-white text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5">
          <ZoomIn size={12}/> Toca para ampliar
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl w-[96vw] p-0 bg-white border-0 overflow-hidden" data-testid="parejas-anatomy-dialog">
          <div className="relative w-full max-h-[92vh] overflow-auto bg-[#F9F8FA]">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-[#2E2638] text-white hover:bg-[#8B1538] transition-colors"
              data-testid="parejas-anatomy-dialog-close"
            >
              <X size={18}/>
            </button>
            <img
              src={src}
              alt="Colchón Core por dentro, ampliado"
              className="w-full h-auto block"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ STICKY MOBILE CTA =============
function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-[#2E2638]/95 to-[#2E2638]/0 pointer-events-none transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      data-testid="parejas-sticky-mobile-cta-wrap"
    >
      <a
        href="#hero-form"
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById("hero-form");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        data-testid="parejas-sticky-mobile-cta"
        className="pointer-events-auto flex items-center justify-center gap-2 w-full bg-[#8B1538] hover:bg-[#6A0F2A] text-white font-bold uppercase tracking-wider text-sm px-5 py-4 shadow-[0_8px_24px_rgba(139,21,56,0.55)]"
      >
        Ven a probar el colchón <ArrowRight size={16}/>
      </a>
    </div>
  );
}

// ============ FOOTER =============
function ParejasFooter() {
  return (
    <footer className="bg-[#1a1522] text-white/70 pb-24 md:pb-0">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-10 grid sm:grid-cols-3 gap-6 items-start">
        <div>
          <img src={LOGO} alt="Core" className="h-10 w-10 object-contain"/>
          <div className="mt-3 text-xs">CORE COLCHÓN — Un producto Muebles de Siempre, S.L.</div>
        </div>
        <div className="text-sm">
          <div className="text-white font-bold mb-2">Tienda</div>
          <div>C/ Dehesa de Mari Martín, 27 (Autovía N-V)</div>
          <div>28600 Navalcarnero, Madrid</div>
          <div className="mt-1">Tel. 910 88 99 62</div>
        </div>
        <div className="text-sm flex flex-wrap gap-x-3 gap-y-1 sm:justify-end">
          <Link to="/" className="hover:text-white">Web principal</Link>
          <span>·</span>
          <Link to="/aviso-legal" className="hover:text-white">Aviso legal</Link>
          <span>·</span>
          <Link to="/privacidad" className="hover:text-white">Privacidad</Link>
          <span>·</span>
          <Link to="/cookies" className="hover:text-white">Cookies</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs">
        © {new Date().getFullYear()} CORE COLCHÓN · Hecho en España.
      </div>
    </footer>
  );
}
